import { PageEvents, WebPageEvents, WebPaths } from '@griply/types';
import Typography from '@mui/material/Typography';
import { useStartDesktopSignIn } from 'features/user';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';

import * as Styled from './desktop-sign-in.style';

export type DesktopSignInProps = {
  pageEvent: PageEvents | WebPageEvents;
  hasFailed?: boolean;
};

export const DesktopSignIn: React.FC<DesktopSignInProps> = ({
  pageEvent,
  hasFailed,
}) => {
  const { t } = useTranslation();
  const { start, browserUrl } = useStartDesktopSignIn();
  useTrackPageVisitEvent(pageEvent, undefined);

  const handleSignIn = useCallback(() => {
    start(WebPaths.Login);
  }, [start]);

  const handleSignUp = useCallback(() => {
    start(WebPaths.Signup);
  }, [start]);

  // A failed hand-off must not silently repaint the same invitation the user already followed.
  const descriptionKey = hasFailed
    ? 'forms.desktop-sign-in.failed.description'
    : browserUrl
      ? 'forms.desktop-sign-in.waiting.description'
      : 'forms.desktop-sign-in.description';

  return (
    <Styled.Container>
      <Styled.Body>
        <Styled.BodyContent>
          <Styled.Header>
            <Styled.Logo
              src="/griply-logo/griply-logo.png"
              srcSet="/griply-logo/griply-logo@2x.png 2x, /griply-logo/griply-logo@3x.png 3x"
              alt="Griply"
            />

            <Typography variant="h3" textAlign="center">
              {t('forms.login.title')}
            </Typography>

            <Styled.Description>{t(descriptionKey)}</Styled.Description>
          </Styled.Header>

          <Styled.Options>
            <Button
              variant={Variants.Contained}
              color={Colors.Primary}
              size={Sizes.Large}
              onClick={handleSignIn}
              fullWidth
              alignCenter
            >
              {t(
                browserUrl
                  ? 'forms.desktop-sign-in.buttons.retry.label'
                  : 'forms.desktop-sign-in.buttons.continue.label',
              )}
            </Button>

            {!!browserUrl && (
              <>
                <Styled.Description>
                  {t('forms.desktop-sign-in.copy-link.label')}
                </Styled.Description>
                <Styled.CopyableUrl>{browserUrl}</Styled.CopyableUrl>
              </>
            )}
          </Styled.Options>
        </Styled.BodyContent>
      </Styled.Body>

      <Styled.Footer>
        <Typography variant="overline" component="span" textAlign="center">
          {t('forms.login.no-account.question.label')}{' '}
          <Styled.SignUpButton type="button" onClick={handleSignUp}>
            {t('forms.login.no-account.link.label')}
          </Styled.SignUpButton>
        </Typography>
      </Styled.Footer>
    </Styled.Container>
  );
};
